import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyIcon from '@mui/icons-material/Key';
import { apiKeysAPI } from '../../services/api';

interface ApiKey {
  id: string;
  key: string;
  name: string;
  url?: string;
  bucket_name?: string;
  bucket_type?: string;
  bucket_region?: string;
  created_at: string;
  status: 'active' | 'disabled';
}

const ApiKeys: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [openKeyDialog, setOpenKeyDialog] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    bucketType: 'AWS' as 'AWS' | 'GCP',
    bucket: '',
    bucketRegion: '',
    secretKey: ''
  });
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Fetch API keys on component mount
  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiKeysAPI.list();
      
      if (response.success) {
        setApiKeys(response.data || []);
      } else {
        setError(response.error || 'Failed to load API keys');
      }
    } catch (err: any) {
      console.error('Error fetching API keys:', err);
      setError(err.response?.data?.error || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({ name: '', url: '', bucketType: 'AWS', bucket: '', bucketRegion: '', secretKey: '' });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleGenerateKey = async () => {
    try {
      setSubmitting(true);
      
      const response = await apiKeysAPI.create({
        name: formData.name,
        url: formData.url || undefined,
        bucket_type: formData.bucketType,
        bucket_name: formData.bucket,
        bucket_region: formData.bucketRegion || undefined,
        secret_key: formData.secretKey
      });

      if (response.success) {
        // Store the new API key and show it in a dialog
        setNewApiKey(response.data.key);
        setOpenKeyDialog(true);
        handleCloseDialog();
        fetchApiKeys(); // Refresh the list
      } else {
        setSnackbar({ open: true, message: response.error || 'Failed to generate API key', severity: 'error' });
      }
    } catch (err: any) {
      console.error('Error generating API key:', err);
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to generate API key', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseKeyDialog = () => {
    setOpenKeyDialog(false);
    setNewApiKey('');
  };

  const handleCopyNewKey = () => {
    navigator.clipboard.writeText(newApiKey);
    setSnackbar({ open: true, message: 'API key copied to clipboard!', severity: 'success' });
  };

  const handleToggleStatus = async (id: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === 'active' ? 'disabled' : 'active';
      
      const response = await apiKeysAPI.update(id, { status: newStatus });

      if (response.success) {
        setSnackbar({ 
          open: true, 
          message: `API key ${newStatus === 'active' ? 'enabled' : 'disabled'} successfully`, 
          severity: 'success' 
        });
        fetchApiKeys(); // Refresh the list
      } else {
        setSnackbar({ open: true, message: response.error || 'Failed to update API key', severity: 'error' });
      }
    } catch (err: any) {
      console.error('Error updating API key:', err);
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to update API key', severity: 'error' });
    }
  };

  const handleCopyKey = (key: string) => {
    navigator.clipboard.writeText(key);
    setSnackbar({ open: true, message: 'API key copied to clipboard!', severity: 'success' });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight={700}>
            API Keys
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your API keys and storage configuration
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          size="large"
        >
          Generate API Key
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Keep your API keys secure. Screenshots will be automatically uploaded to your configured bucket.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : apiKeys.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <KeyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No API Keys Yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Generate your first API key to start capturing screenshots
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
            >
              Generate Your First API Key
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer>
              <Table sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>API Key</strong></TableCell>
                    <TableCell><strong>URL</strong></TableCell>
                    <TableCell><strong>Storage</strong></TableCell>
                    <TableCell><strong>Bucket</strong></TableCell>
                    <TableCell><strong>Created</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {apiKeys.map((apiKey) => (
                    <TableRow key={apiKey.id}>
                      <TableCell>{apiKey.name}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <code style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                          {apiKey.key ? `${apiKey.key.substring(0, 20)}...` : '••••••••••••••••••••'}
                        </code>
                        {apiKey.key && (
                          <IconButton size="small" onClick={() => handleCopyKey(apiKey.key)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                      <TableCell>
                        <Box sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {apiKey.url || '-'}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {apiKey.bucket_type ? (
                          <Chip 
                            label={apiKey.bucket_type.toUpperCase()} 
                            size="small"
                            color={apiKey.bucket_type.toUpperCase() === 'AWS' ? 'warning' : 'info'}
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell>{apiKey.bucket_name || '-'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {new Date(apiKey.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={apiKey.status === 'active' ? <CheckCircleIcon /> : <BlockIcon />}
                          label={apiKey.status === 'active' ? 'Active' : 'Disabled'}
                          color={apiKey.status === 'active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="outlined"
                          size="small"
                          color={apiKey.status === 'active' ? 'error' : 'success'}
                          onClick={() => handleToggleStatus(apiKey.id, apiKey.status)}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          {apiKey.status === 'active' ? 'Disable' : 'Enable'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Card>
      )}

      {/* Generate API Key Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Generate New API Key</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              fullWidth
              label="API Key Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Production API"
              required
            />
            <TextField
              fullWidth
              label="Website URL (Optional)"
              name="url"
              value={formData.url}
              onChange={handleChange}
              placeholder="https://example.com"
            />
            <TextField
              fullWidth
              select
              label="Storage Provider"
              name="bucketType"
              value={formData.bucketType}
              onChange={handleChange}
              required
            >
              <MenuItem value="AWS">Amazon Web Services (AWS)</MenuItem>
              <MenuItem value="GCP">Google Cloud Platform (GCP)</MenuItem>
            </TextField>
            <TextField
              fullWidth
              label="Bucket Name"
              name="bucket"
              value={formData.bucket}
              onChange={handleChange}
              placeholder={formData.bucketType === 'AWS' ? 'my-s3-bucket' : 'my-gcs-bucket'}
              helperText={`Enter your ${formData.bucketType} bucket name where screenshots will be uploaded`}
              required
            />
            <TextField
              fullWidth
              label="Bucket Region (Optional)"
              name="bucketRegion"
              value={formData.bucketRegion}
              onChange={handleChange}
              placeholder={formData.bucketType === 'AWS' ? 'us-east-1' : 'us-central1'}
              helperText={`Enter your ${formData.bucketType} bucket region`}
            />
            <TextField
              fullWidth
              label={formData.bucketType === 'AWS' ? 'AWS Secret Access Key' : 'GCP Service Account Key'}
              name="secretKey"
              type="password"
              value={formData.secretKey}
              onChange={handleChange}
              placeholder={formData.bucketType === 'AWS' ? 'Enter AWS Secret Key' : 'Enter GCP JSON Key'}
              helperText={`Enter your ${formData.bucketType} credentials for bucket access`}
              multiline={formData.bucketType === 'GCP'}
              rows={formData.bucketType === 'GCP' ? 3 : 1}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDialog} disabled={submitting}>Cancel</Button>
          <Button 
            onClick={handleGenerateKey} 
            variant="contained"
            disabled={!formData.name || !formData.bucket || !formData.secretKey || submitting}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {submitting ? 'Generating...' : 'Generate Key'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* API Key Created Dialog */}
      <Dialog open={openKeyDialog} onClose={handleCloseKeyDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'black', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon />
            <Typography variant="h6">API Key Generated Successfully!</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>Important:</strong> This is the only time you'll see this API key. Make sure to copy it now!
          </Alert>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Your API Key:
          </Typography>
          
          <Paper 
            sx={{ 
              p: 2, 
              bgcolor: 'grey.100', 
              fontFamily: 'monospace',
              wordBreak: 'break-all',
              mb: 2,
              border: '2px solid',
              borderColor: 'black'
            }}
          >
            <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
              {newApiKey}
            </Typography>
          </Paper>

          <Button
            variant="contained"
            fullWidth
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyNewKey}
            size="large"
          >
            Copy to Clipboard
          </Button>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseKeyDialog} variant="outlined" fullWidth>
            I've Saved My API Key
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ApiKeys;

